import { useState, type CSSProperties } from 'react';
import { track } from '@vercel/analytics';
import { useLanguage } from '../../contexts/LanguageContext';
import { FeedbackModal } from '../FeedbackModal';

interface GlobalNavProps {
  onBackToHome?: () => void;
}

const PALETTE = {
  cream: '#faf7ef',
  creamDeep: '#f2ecdc',
  paper: '#ffffff',
  ink: '#1a2420',
  inkMuted: '#6b7770',
  gold: '#c9a961',
  goldSoft: '#e8d9a8',
  green: '#42a57a',
} as const;

const Serif: CSSProperties = { fontFamily: "'Fraunces', Georgia, serif" };
const Sans: CSSProperties = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };
const Mono: CSSProperties = { fontFamily: "'JetBrains Mono', ui-monospace, monospace" };

const WonderpathMark = ({ size = 42, color = PALETTE.green }: { size?: number; color?: string }) => {
  const h = size;
  const w = size * (184.77 / 238.44);
  return (
    <svg width={w} height={h} viewBox="0 0 184.77 238.44" fill="none" style={{ display: 'block', flexShrink: 0 }}>
      <path
        d="M26.16,92.53c4.46,109.26,121.77,193.97,142.8,72.49,6.86-39.63.51-79.13-8.17-105.85-20.89-64.33-105.56-67.61-125.84,0C15.4,124.36,5.31,203.58,47.74,204.93c24.87.79,96.07-106.15,74.9-146.96-9.29-17.91-38.27-25.75-51.46-3.82-5.49,9.12-4.55,15.41-.81,23.38,5.7,12.15,18.2,10.55,24.03,7.59s8.15-10.08,5.19-15.91"
        fill="none"
        stroke={color}
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const GlobeIcon = ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
  </svg>
);

const ChatIcon = ({ size = 18, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const BetaSticker = ({ scale = 1 }: { scale?: number }) => (
  <span
    style={{
      ...Mono,
      fontSize: 12 * scale * 0.93,
      color: PALETTE.ink,
      background: PALETTE.goldSoft,
      padding: `${4 * scale * 0.93}px ${10 * scale * 0.93}px`,
      borderRadius: 999,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      fontWeight: 600,
      transform: 'rotate(-4deg)',
      display: 'inline-block',
      border: `1.2px solid ${PALETTE.gold}`,
      whiteSpace: 'nowrap',
    }}
  >
    Beta
  </span>
);

const pillBtnStyle: CSSProperties = {
  ...Sans,
  fontSize: 15,
  fontWeight: 500,
  cursor: 'pointer',
  background: 'transparent',
  color: PALETTE.green,
  padding: '12px 18px',
  borderRadius: 999,
  border: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  minHeight: 46,
  transition: 'background-color 160ms ease-out',
};

/**
 * GlobalNav — the single, shared top navigation across the entire app
 * (homepage and every game/experience). Cream-bg pill containing the
 * Wonderpath wordmark + Beta sticker, "Creative EcoGames" sub-brand,
 * language toggle, and Feedback button. Use this everywhere — there is
 * no other nav variant.
 */
export function GlobalNav({ onBackToHome }: GlobalNavProps) {
  const { language, setLanguage } = useLanguage();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const openFeedback = () => {
    track('Feedback Modal Opened', { source: 'global-nav', language });
    setIsFeedbackOpen(true);
    setMobileMenuOpen(false);
  };

  const brandClickProps = onBackToHome
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => {
          track('Navigation', { action: 'back-to-home', source: 'global-nav' });
          onBackToHome();
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            track('Navigation', { action: 'back-to-home', source: 'global-nav' });
            onBackToHome();
          }
        },
      }
    : {};
  const brandClickCursor: CSSProperties = onBackToHome ? { cursor: 'pointer' } : {};

  const setLang = (next: 'en' | 'es') => {
    if (language === next) return;
    setLanguage(next);
    track('Language Changed', { language: next, source: 'global-nav' });
  };

  const LangSegment = ({ compact = false }: { compact?: boolean }) => {
    const baseBtn: CSSProperties = {
      ...Sans,
      fontSize: compact ? 13 : 14,
      fontWeight: 600,
      cursor: 'pointer',
      border: 'none',
      padding: compact ? '8px 14px' : '10px 16px',
      minHeight: compact ? 36 : 40,
      transition: 'background-color 160ms ease-out, color 160ms ease-out',
    };
    return (
      <div
        role="group"
        aria-label="Language"
        style={{
          display: 'inline-flex',
          alignItems: 'stretch',
          border: `1.5px solid ${PALETTE.green}`,
          borderRadius: 999,
          overflow: 'hidden',
          background: 'transparent',
        }}
      >
        <button
          type="button"
          onClick={() => setLang('en')}
          aria-pressed={language === 'en'}
          aria-label="Switch to English"
          style={{
            ...baseBtn,
            background: language === 'en' ? PALETTE.green : 'transparent',
            color: language === 'en' ? PALETTE.paper : PALETTE.green,
          }}
        >
          English
        </button>
        <button
          type="button"
          onClick={() => setLang('es')}
          aria-pressed={language === 'es'}
          aria-label="Cambiar a español"
          style={{
            ...baseBtn,
            background: language === 'es' ? PALETTE.green : 'transparent',
            color: language === 'es' ? PALETTE.paper : PALETTE.green,
            borderLeft: `1.5px solid ${PALETTE.green}`,
          }}
        >
          Español
        </button>
      </div>
    );
  };

  return (
    <>
      {/* DESKTOP */}
      <div
        className="hidden md:block"
        style={{ padding: '24px 36px', background: PALETTE.cream, position: 'relative' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: PALETTE.paper,
            borderRadius: 999,
            padding: '12px 14px 12px 26px',
            border: `1.5px solid ${PALETTE.green}`,
            boxShadow: `4px 4px 0 ${PALETTE.green}`,
            gap: 24,
          }}
        >
          {/* LEFT: brand lockup */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0, ...brandClickCursor }}
            {...brandClickProps}
          >
            <WonderpathMark size={42} />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
              <span
                style={{
                  ...Serif,
                  fontSize: 26,
                  color: PALETTE.green,
                  fontWeight: 700,
                  letterSpacing: '-0.01em',
                  lineHeight: 1,
                }}
              >
                Wonderpath
              </span>
              <BetaSticker />
            </div>
            <div style={{ width: 1.5, height: 32, background: PALETTE.creamDeep }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 20 }} aria-hidden="true">
                  🌱
                </span>
                <span
                  style={{
                    ...Serif,
                    fontSize: 20,
                    color: PALETTE.green,
                    fontWeight: 600,
                    fontStyle: 'italic',
                    lineHeight: 1,
                  }}
                >
                  Creative EcoGames
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT: utilities */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LangSegment />
            <button type="button" style={pillBtnStyle} onClick={openFeedback}>
              <ChatIcon size={18} color={PALETTE.green} />
              <span>Feedback</span>
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE */}
      <div
        className="md:hidden"
        style={{ padding: '12px 14px 14px', background: PALETTE.cream, position: 'relative' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: PALETTE.paper,
            borderRadius: 999,
            padding: '8px 8px 8px 14px',
            border: `1.5px solid ${PALETTE.green}`,
            boxShadow: `3px 3px 0 ${PALETTE.green}`,
            gap: 10,
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, ...brandClickCursor }}
            {...brandClickProps}
          >
            <WonderpathMark size={32} />
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{
                    ...Serif,
                    fontSize: 19,
                    color: PALETTE.green,
                    fontWeight: 700,
                    fontStyle: 'italic',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Creative EcoGames
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                <span
                  style={{
                    ...Sans,
                    fontSize: 14,
                    color: PALETTE.inkMuted,
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Wonderpath
                </span>
                <BetaSticker scale={0.92} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((v) => !v)}
              style={{
                cursor: 'pointer',
                background: 'transparent',
                color: PALETTE.green,
                border: `1.5px solid ${PALETTE.green}`,
                borderRadius: 999,
                width: 44,
                height: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke={PALETTE.green}
                strokeWidth="2"
                strokeLinecap="round"
              >
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div
            style={{
              marginTop: 10,
              background: PALETTE.paper,
              border: `1.5px solid ${PALETTE.green}`,
              borderRadius: 20,
              boxShadow: `3px 3px 0 ${PALETTE.green}`,
              padding: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
              }}
            >
              <GlobeIcon size={18} color={PALETTE.green} />
              <LangSegment compact />
            </div>
            <button
              type="button"
              onClick={openFeedback}
              style={{
                ...Sans,
                fontSize: 15,
                fontWeight: 500,
                cursor: 'pointer',
                background: 'transparent',
                color: PALETTE.green,
                border: 'none',
                borderRadius: 14,
                padding: '14px 16px',
                minHeight: 52,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                textAlign: 'left',
              }}
            >
              <ChatIcon size={18} color={PALETTE.green} />
              <span>{language === 'es' ? 'Comparte tu Feedback' : 'Share Feedback'}</span>
            </button>
          </div>
        )}
      </div>

      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  );
}
